import React, {useCallback, useEffect, useRef, useState} from 'react';
import {BASE_URL, useQueryToken} from '../../utils/dynamicCodes/hooks';
import axios, {AxiosError} from 'axios';
import validateEmail from '../../utils/validateEmail';
import {CardElement, PaymentRequestButtonElement, useElements, useStripe} from '@stripe/react-stripe-js';
import CustomPaymentRequestButton from '../../components/CustomPaymentRequestButton';
import {PaymentRequest, PaymentRequestPaymentMethodEvent} from '@stripe/stripe-js';
import {stripeStyle} from './stripeStyle';
import ButtonProcessing from '../../components/ButtonProcessing';
import Analytics from '../../utils/Analytics';
import MarketingEvents from '../../utils/MarketingEvents';
import Cookies from 'js-cookie';
import Environment from '../../utils/Environment';
import {
  redirectToSuccess,
  reportRepeatCheckout,
  UserAlreadySubscribedMessage,
} from '../../utils/dynamicCodes/redirects';
import * as Sentry from '@sentry/react';
import {Scroll} from '../../components';
import i18n from '../../i18n/i18n';
import {useTranslation} from 'react-i18next';
import {growthBook} from '../../index';
import {extractCheckoutError} from './extractCheckoutError';
import SecondChanceOfferSheet from '../../second-chance-offer/SecondChanceOfferSheet';
import {SecondChanceOffer} from '../../second-chance-offer/secondChanceOfferPrices';
import {PAYWALL_EVENTS, PaywallSlug} from '../../analytics/paywallEvents';
import {getSearchParams} from '../../utils/urlQuery';
import {tokenParam} from '../../utils/linkOutSession';

const sendsToSentryIfUncommon = (error: any) => {
  if (error.response?.data === UserAlreadySubscribedMessage) {
    return;
  }
  if (error.response?.data?.error === 'Email already taken.') {
    return;
  }
  Sentry.captureException(new Error(error));
};

const completeCheckout = async (
  /**  Dynamic Checkout is used for our iOS/Stripe Applications
   *    - does things like apply tax and other...things
   * */
  isDynamicCheckout: boolean,
  code: string,
  token: string,
  email: string,
  payment_method_id: string,
  winback = false,
) => {
  const dynamicCheckoutEndpoint = `${BASE_URL}/api/stripe-dynamic-checkout`;
  const standardCheckoutEndpoint = `${BASE_URL}/api/stripe-complete-checkout`;
  const endpoint = isDynamicCheckout ? dynamicCheckoutEndpoint : standardCheckoutEndpoint;

  Analytics.track('Attempting to complete custom Stripe checkout', {code});
  try {
    await axios.post(endpoint, {
      token,
      payment_method_id,
      email,
      code,
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      winback,
    });
    Analytics.track('Stripe Checkout Completed', {code});
    return {error: null, success: true};
  } catch (error) {
    sendsToSentryIfUncommon(error);
    Analytics.track('Failed to complete custom Stripe checkout', {
      ...extractCheckoutError(error),
      stage: 'complete_checkout',
      code,
    });
    return {error: error as AxiosError, success: false};
  }
};

const completeLifetimePurchase = async (
  token: string,
  email: string,
  payment_method_id: string,
  setup_intent_id: string,
  price_id: string,
) => {
  const endpoint = `${BASE_URL}/api/stripe-lifetime-purchase`;

  Analytics.track('Attempting to complete lifetime purchase', {price_id});
  try {
    await axios.post(endpoint, {
      token,
      payment_method_id,
      email,
      setup_intent_id,
      price_id,
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
    });
    Analytics.track('Lifetime Purchase Completed', {price_id});
    return {error: null, success: true};
  } catch (error) {
    sendsToSentryIfUncommon(error);
    Analytics.track('Failed to complete lifetime purchase', {
      ...extractCheckoutError(error),
      stage: 'complete_checkout',
      price_id,
    });
    return {error: error as AxiosError, success: false};
  }
};

const buttonText = (isWinback: boolean, daysFree: number | undefined) => {
  if (isWinback) {
    return i18n.t('checkout_screen.redeem_offer');
  }
  if (daysFree) {
    if (daysFree === 7) {
      return i18n.t('checkout_screen.redeem_1_week_free');
    }
    return i18n.t('checkout_screen.redeem_x_days_free', {
      daysFree,
    });
  } else if (daysFree === 0) {
    return i18n.t('checkout_screen.redeem_offer');
  }
  return i18n.t('checkout_screen.redeem_free_week');
};

const isWinback = () => {
  const params = getSearchParams();
  return Boolean(params.get('winback'));
};

const isSurvey = () => {
  const params = getSearchParams();
  return Boolean(params.get('survey'));
};

const isParent = () => {
  const params = getSearchParams();
  return Boolean(params.get('parent'));
};

export interface PaymentIntentParams {
  code: string;
  paymentRequest?: PaymentRequest;
  clientSecret?: string;
  /**
   * For a screen that shows the payment UI before `stripe-setup-checkout` has answered: resolves to the SetupIntent's
   * client secret once it has (null if it failed). A card checkout waits for it when `clientSecret` isn't set yet.
   * Wallet checkouts never need it.
   */
  getClientSecret?: () => Promise<string | null>;
  token?: string | null;
  daysFree?: number;
  chargeDate?: string | null;
  survey?: boolean;
  parent?: boolean;
  ctaText?: string;
  dynamicCheckout?: boolean; //use this for iOS/Stripe Applications - add does other stuff like applies tax and :hand wavy: things.
  onPurchaseSuccess?: ({existingAccount}: {existingAccount: boolean}) => void;
  onPurchaseFailure?: () => void;
  onPrepaidDetected?: (mode: 'disallow_try_again' | 'disallow_and_redirect') => void;
  paymentRequestLoading?: boolean;
  onCreditCardChoice?: () => void;
  useUpdatedCreditCardLogic?: boolean;
  allowRepeatPurchase?: boolean;
  useCustomCTA?: boolean;
  customCTAText?: string;
  lifetimePurchase?: boolean;
  lifetimePriceId?: string;
  // I21 — second-chance offer sheet. Opened only by the parent's dismiss affordance (see
  // `secondChanceOpenerRef`); accepting re-shows the pay sheet and completes checkout
  // against `secondChanceOffer.code` instead of the original `code`. Purely additive —
  // with no prop, behavior is unchanged.
  secondChanceOffer?: SecondChanceOffer;
  onSecondChanceSuccess?: () => void;
  // Called after a second-chance attempt ends without a purchase and the pay sheet has
  // closed. The accept handler mutates the shared paymentRequest to the offer's terms, so
  // the owner (parent) must restore it to the currently-selected option's terms —
  // otherwise a later normal checkout would show stale offer pricing on the native sheet.
  onSecondChanceDismissed?: () => void;
  // I21 — let a parent open the second-chance sheet from a screen-level dismiss affordance
  // (a corner ✕ or an idle-time timer). PaymentIntentScreen assigns an opener into
  // `ref.current`; the parent invokes it on its dismiss trigger, passing a `source` label
  // for analytics. Persistent/re-openable.
  secondChanceOpenerRef?: React.MutableRefObject<((source?: string) => void) | undefined>;
  // I21 — called when the user starts checking out (taps the CTA or picks the credit card
  // form), so the parent's idle-time timer stops (see useDismissTreatment).
  onCheckoutStarted?: () => void;
  /**
   * Paywall slug attached to shared payment events (e.g. Payment Sheet Dismissed) so they
   * can be attributed to the screen that rendered this component. See paywallEvents.ts.
   */
  paywall?: PaywallSlug;
}

const PaymentIntentScreen = ({
  code,
  paymentRequest,
  clientSecret,
  getClientSecret,
  daysFree,
  chargeDate,
  ctaText,
  dynamicCheckout,
  onPurchaseSuccess,
  onPurchaseFailure,
  onPrepaidDetected,
  paymentRequestLoading,
  onCreditCardChoice,
  useUpdatedCreditCardLogic = false,
  allowRepeatPurchase = false,
  useCustomCTA = false,
  customCTAText,
  lifetimePurchase = false,
  lifetimePriceId,
  secondChanceOffer,
  onSecondChanceSuccess,
  onSecondChanceDismissed,
  secondChanceOpenerRef,
  onCheckoutStarted,
  paywall,
}: PaymentIntentParams) => {
  const {t} = useTranslation();
  const [shouldAskForEmail, setShouldAskForEmail] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [accountCreationError, setAccountCreationError] = useState<boolean>(false);
  const [stripeError, setStripeError] = useState<string>('');
  const [processing, setProcessing] = useState<boolean>(false);

  const createdAccount = useRef(false);
  const [accountCreationEmail, setAccountCreationEmail] = useState<string | null>(null);

  const [showCreditCardInput, setShowCreditCardInput] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<string>('');

  // QA-only: force the offer sheet open on mount so it can be verified without a real
  // Apple/Google Pay cancel (which can't happen on desktop). Opt in with
  // ?qaForceDismissOffer=1 (pair with ?qaForceCTA=1 so this screen renders). Hard-
  // disabled in production.
  const qaForceDismissOffer =
    !Environment.isProduction && typeof window !== 'undefined' && getSearchParams().get('qaForceDismissOffer') === '1';
  const [showSecondChanceOffer, setShowSecondChanceOffer] = useState<boolean>(qaForceDismissOffer);
  // True while a second-chance purchase is in flight (the re-shown pay sheet), so the
  // paymentmethod handler completes against the offer code instead of the original.
  const secondChanceActiveRef = useRef(false);
  // Set whenever the shared paymentRequest carries the offer's terms (the accept handler
  // rewrote them) and they haven't been put back yet — however the attempt ended: cancel,
  // failed payment, blocked prepaid card or a show() rejection. Stripe rejects update()
  // while the pay sheet is showing (e.g. inside its cancel event), so the restore is
  // deferred to when the offer sheet closes — by then the pay sheet is gone.
  const restorePendingRef = useRef(false);
  // What triggered the currently-shown second-chance offer, tagged onto every dismiss-
  // offer analytics event. The I21 opener sets it to the affordance/idle source.
  const secondChanceSourceRef = useRef<string>('dismiss_affordance');

  const elements = useElements();
  const stripe = useStripe();
  let token = useQueryToken();

  const el = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (token) {
      // this is doing double duty - checking if token is invalid and/or a phone number
      const isValid = validateEmail(token);
      setShouldAskForEmail(!isValid);
    } else {
      setShouldAskForEmail(true);
    }
  }, [token]);

  // Determine which payment method is available
  useEffect(() => {
    const determinePaymentMethod = async () => {
      if (paymentRequest) {
        try {
          const canMakePayment = await paymentRequest.canMakePayment();
          if (canMakePayment) {
            if (canMakePayment.applePay) {
              setPaymentMethod('Apple Pay');
            } else if (canMakePayment.googlePay) {
              setPaymentMethod('Google Pay');
            }
          } else {
            // No payment method available, fallback to credit card
            setPaymentMethod('credit card');
          }
        } catch (error) {
          console.error('Error determining payment method:', error);
          setPaymentMethod('credit card');
        }
      } else {
        // No payment request available, fallback to credit card
        setPaymentMethod('credit card');
      }
    };

    determinePaymentMethod();
  }, [paymentRequest]);

  const createAccount = useCallback(
    async (email: string) => {
      if (createdAccount.current && email === accountCreationEmail) {
        Analytics.track('Skipping account creation with identical email', {
          email,
          code,
        });
        return {error: null, success: true};
      }

      try {
        const response = await axios.post(`${BASE_URL}/api/web-create-account`, {
          signUpSource: 'web',
          email,
          marketingSource: {
            code,
          },
        });
        if (response.status !== 200) {
          throw new Error(`Status: ${response.status} ${response.statusText}`);
        }
        createdAccount.current = true;
        setAccountCreationEmail(email);
        const currentTimeMs = Math.floor(Date.now());
        const {person_id: personId} = response.data;

        Analytics.identify(personId, {
          email,
          marketingSourceCode: code,
        });

        Analytics.track('Web Sign Up Success', {
          marketingSourceCode: code,
        });

        const eventId = `sign_up_${personId}_${currentTimeMs}`;

        MarketingEvents.sendMarketingEvent(
          MarketingEvents.FB_STANDARD_EVENTS.COMPLETE_REGISTRATION,
          {},
          {eventID: eventId},
        );
        MarketingEvents.sendConversionAPIEvent(MarketingEvents.FB_STANDARD_EVENTS.COMPLETE_REGISTRATION, eventId, {
          em: email,
          external_id: `${personId}`,
          fbp: Cookies.get('_fbp'),
          // fbc: Cookies.get('_fbc')
          // what about fbc? we don't appear to be handling this
        });
        return {error: null, success: true};
      } catch (error) {
        // An error here is likely from using an email address already in our system
        sendsToSentryIfUncommon(error);
        Analytics.track('Web Sign Up Error', {
          error,
          marketingSourceCode: code,
          email,
        });
        setAccountCreationError(true);
        return {error, success: false};
      }
    },
    [accountCreationEmail, code],
  );

  // Apple Pay Handler: If user can use Apple/Google Pay, set up the payment request handler
  useEffect(() => {
    // Whether the user authorized a payment method (fired 'paymentmethod') before
    // dismissing the sheet — lets us tell a cold dismiss apart from a dismiss after
    // a failed/abandoned attempt on the 'Payment Sheet Dismissed' event below.
    let attemptedPayment = false;
    // Stripe can emit 'cancel' *after* a successful purchase in some browsers (see
    // payment-request.d.ts). This flag lets analysis discard those spurious dismisses.
    let completedPurchase = false;
    const handler = async (event: PaymentRequestPaymentMethodEvent) => {
      attemptedPayment = true;
      // If this authorization came from the second-chance offer sheet, complete against
      // the offer's price code rather than the original. Read+reset the flag up front so
      // re-entrancy is clean.
      const isSecondChance = secondChanceActiveRef.current && !!secondChanceOffer;
      secondChanceActiveRef.current = false;
      const activeCode = isSecondChance && secondChanceOffer ? secondChanceOffer.code : code;
      Analytics.track(`Attempt to use ${event.walletName}`, {code: activeCode, secondChance: isSecondChance});
      // this is utilized by the Apple/Google Play flow - creates an account if necessary and validates the Payment Method.
      // Creates a Subscription via Hypnos before redirecting to our generic success page - DynamicSuccess

      const {paymentMethod, payerEmail} = event;

      // Log card funding type for every Apple/Google Pay transaction
      const cardFunding = paymentMethod.card?.funding;
      Analytics.track('Payment Method Card Info', {
        code: activeCode,
        secondChance: isSecondChance,
        funding: cardFunding,
        brand: paymentMethod.card?.brand,
        country: paymentMethod.card?.country,
        wallet: event.walletName,
      });

      // Block prepaid cards based on GrowthBook flag
      // 'allow' = let all cards through (default), 'disallow_try_again' = block + prompt retry, 'disallow_and_redirect' = block + send to app
      const prepaidCardPolicy = growthBook.getFeatureValue('web_payments_prepaid_card_policy', 'allow');
      const shouldBlockPrepaid =
        prepaidCardPolicy === 'disallow_try_again' || prepaidCardPolicy === 'disallow_and_redirect';
      if (cardFunding === 'prepaid' && shouldBlockPrepaid) {
        Analytics.track('Prepaid Card Blocked', {
          code,
          env: Environment.getVar('REACT_APP_ENV') ?? 'test',
          funding: cardFunding,
          brand: paymentMethod.card?.brand,
          country: paymentMethod.card?.country,
          wallet: event.walletName,
          policy: prepaidCardPolicy,
        });
        event.complete('fail');
        onPrepaidDetected?.(prepaidCardPolicy as 'disallow_try_again' | 'disallow_and_redirect');
        return;
      }

      let personIdentifier = token;

      if (personIdentifier == null && payerEmail) {
        const {error} = await createAccount(payerEmail);
        if (error) {
          setAccountCreationError(true);
          Analytics.track(`Failed to use ${event.walletName}`, {
            ...extractCheckoutError(error),
            stage: 'account_creation',
            code,
          });
          event.complete('fail');
          return;
        } else {
          personIdentifier = payerEmail;
        }
      }

      if (!personIdentifier) {
        Analytics.track(`Failed to use ${event.walletName}`, {
          error: 'Token unexpectedly null',
          code,
        });
        sendsToSentryIfUncommon('Token unexpectedly null during checkout');
        event.complete('fail');
        return;
      }

      let error;
      if (lifetimePurchase && lifetimePriceId) {
        const result = await completeLifetimePurchase(
          personIdentifier,
          payerEmail ?? '',
          paymentMethod.id,
          '', // setup_intent_id not available in paymentRequest flow, will be handled by backend
          lifetimePriceId,
        );
        error = result.error;
      } else {
        const result = await completeCheckout(
          dynamicCheckout ?? false,
          activeCode,
          personIdentifier,
          payerEmail ?? '', // email should be '' when going through Apple Pay / Google Pay
          paymentMethod.id,
          allowRepeatPurchase,
        );
        error = result.error;
      }

      if (error == null) {
        completedPurchase = true;
        Analytics.track(`Successfully used ${event.walletName}`, {code: activeCode, secondChance: isSecondChance});
        if (isSecondChance) {
          Analytics.track(PAYWALL_EVENTS.dismissOfferPurchaseSuccess, {
            paywall,
            code,
            offerCode: activeCode,
            wallet: event.walletName,
            source: secondChanceSourceRef.current,
          });
        }
        event.complete('success');
        if (isSecondChance && onSecondChanceSuccess) {
          onSecondChanceSuccess();
        } else if (onPurchaseSuccess) {
          onPurchaseSuccess({existingAccount: false});
        } else {
          window.location.href = `${document.location.origin}/offer/success?${tokenParam(personIdentifier, {
            encode: true,
          })}code=${activeCode}${isWinback() ? `&chargeDate=${chargeDate}` : ''}${isSurvey() ? `&survey=1` : ''}${
            isParent() ? `&parent=1` : ''
          }`;
        }
      } else if (error.response?.data === UserAlreadySubscribedMessage) {
        completedPurchase = true;
        reportRepeatCheckout(activeCode, personIdentifier);
        if (isSecondChance && onSecondChanceSuccess) {
          onSecondChanceSuccess();
        } else if (onPurchaseSuccess) {
          onPurchaseSuccess({existingAccount: true});
        } else {
          redirectToSuccess(activeCode, personIdentifier);
        }
      } else {
        Analytics.track(`Failed to use ${event.walletName}`, {
          ...extractCheckoutError(error),
          stage: 'complete_checkout',
          code: activeCode,
          secondChance: isSecondChance,
        });
        event.complete('fail');
        onPurchaseFailure && onPurchaseFailure();
      }
    };

    // Fires when the user closes the native Apple/Google Pay sheet without
    // authorizing (or dismisses after a failed attempt). This is the cleanest
    // "dismiss" intent signal on checkout — the user already committed to the
    // payment step. It previously landed in a bare console.error in
    // CustomPaymentRequestButton and was invisible in analytics. (I21)
    const cancelHandler = () => {
      // Captured before the reset below: true iff the sheet just dismissed was the
      // second-chance re-show (its terms were mutated to the offer in the accept handler).
      Analytics.track(PAYWALL_EVENTS.paymentSheetDismissed, {
        env: Environment.getVar('REACT_APP_ENV') ?? 'test',
        paywall,
        code,
        attemptedPayment,
        completedPurchase,
      });
      // A cancel also ends any in-flight second-chance attempt.
      secondChanceActiveRef.current = false;
      // The sheet can be reopened within the same screen session (repeated
      // paymentRequest.show() calls). Reset per-sheet state so each open is scored
      // on its own — otherwise a prior authorization would make a later cold
      // dismiss look like attemptedPayment: true.
      attemptedPayment = false;
      completedPurchase = false;
    };

    // Listen for event to handle Apple/Google Pay
    if (paymentRequest) {
      paymentRequest.on('paymentmethod', handler);
      paymentRequest.on('cancel', cancelHandler);
    }

    return () => {
      // Unsubscribe from the event when the useEffect is re-rendered
      if (paymentRequest) {
        paymentRequest.off('paymentmethod', handler);
        paymentRequest.off('cancel', cancelHandler);
      }
    };
  }, [
    allowRepeatPurchase,
    chargeDate,
    code,
    createAccount,
    dynamicCheckout,
    onPrepaidDetected,
    onPurchaseFailure,
    onPurchaseSuccess,
    onSecondChanceSuccess,
    paymentRequest,
    token,
    lifetimePurchase,
    lifetimePriceId,
    secondChanceOffer,
    paywall,
  ]);

  // I21 — open the second-chance sheet from a parent-owned dismiss affordance (corner ✕
  // or idle timer). Hands the opener up via `secondChanceOpenerRef`. Not one-shot: the
  // persistent ✕ can reopen the offer repeatedly. No-op if no offer is configured.
  useEffect(() => {
    if (!secondChanceOpenerRef) return;
    secondChanceOpenerRef.current = (source?: string) => {
      if (!secondChanceOffer) return;
      secondChanceSourceRef.current = source ?? 'dismiss_affordance';
      Analytics.track(PAYWALL_EVENTS.dismissOfferShown, {
        paywall,
        code,
        offerCode: secondChanceOffer.code,
        source: secondChanceSourceRef.current,
      });
      setShowSecondChanceOffer(true);
    };
    return () => {
      if (secondChanceOpenerRef) secondChanceOpenerRef.current = undefined;
    };
  }, [secondChanceOpenerRef, secondChanceOffer, code, paywall]);

  // Card checkout, shared by the paywall's card form and the I21 offer sheet's card form:
  // creates an account if necessary, confirms the Setup Intent, then creates the
  // subscription via Hypnos for `activeCode` (the SetupIntent only saves the card; the
  // price is applied server-side by code, so the offer needs no new clientSecret).
  const runCardCheckout = async (activeCode: string, isSecondChance: boolean) => {
    setProcessing(true);

    Analytics.track(`Attempt to use credit card`, {code: activeCode, secondChance: isSecondChance});

    if (stripe === null || elements === null || (clientSecret === undefined && getClientSecret === undefined)) {
      setProcessing(false);
      return;
    }

    const card = elements.getElement(CardElement);
    if (card === null) {
      setProcessing(false);
      return;
    }

    let personIdentifier = token;

    const needToCreateAccount = email && personIdentifier == null;

    if (needToCreateAccount) {
      // If user has already gone through flow, but encounters a Stripe issue, skip Account Creation
      const {error} = await createAccount(email);
      if (error) {
        setAccountCreationError(true);
        setProcessing(false);
        Analytics.track(`Failed to use credit card`, {
          ...extractCheckoutError(error),
          stage: 'account_creation',
          code: activeCode,
          secondChance: isSecondChance,
        });
        return;
      } else {
        personIdentifier = email;
      }
    }

    if (!personIdentifier) {
      setProcessing(false);
      return;
    }

    const setupSecret = clientSecret || (getClientSecret && (await getClientSecret()));
    if (!setupSecret) {
      Analytics.track(`Failed to use credit card`, {
        stage: 'setup_intent',
        code: activeCode,
        secondChance: isSecondChance,
      });
      setStripeError('Could not complete checkout');
      setProcessing(false);
      return;
    }

    const result = await stripe.confirmCardSetup(setupSecret, {
      payment_method: {
        card,
      },
    });

    if (!result.error && result.setupIntent?.payment_method) {
      let error;
      if (lifetimePurchase && lifetimePriceId) {
        const purchaseResult = await completeLifetimePurchase(
          personIdentifier,
          email,
          result.setupIntent.payment_method as string,
          result.setupIntent.id,
          lifetimePriceId,
        );
        error = purchaseResult.error;
      } else {
        const checkoutResult = await completeCheckout(
          dynamicCheckout ?? false,
          activeCode,
          personIdentifier,
          email,
          result.setupIntent.payment_method as string,
          allowRepeatPurchase,
        );
        error = checkoutResult.error;
      }
      if (!error && isSecondChance) {
        Analytics.track(PAYWALL_EVENTS.dismissOfferPurchaseSuccess, {
          paywall,
          code,
          offerCode: activeCode,
          wallet: 'card',
          source: secondChanceSourceRef.current,
        });
        onSecondChanceSuccess && onSecondChanceSuccess();
      } else if (!error) {
        if (onPurchaseSuccess) {
          onPurchaseSuccess && onPurchaseSuccess({existingAccount: false});
        } else {
          window.location.href = `${document.location.origin}/offer/success?${tokenParam(personIdentifier, {
            encode: true,
          })}code=${code}${isWinback() ? `&chargeDate=${chargeDate}` : ''}${isSurvey() ? `&survey=1` : ''}${
            isParent() ? `&parent=1` : ''
          }`;
        }
      } else if (error.response?.data === UserAlreadySubscribedMessage) {
        reportRepeatCheckout(activeCode, email);
        if (onPurchaseSuccess) {
          onPurchaseSuccess && onPurchaseSuccess({existingAccount: true});
        } else {
          redirectToSuccess(code, email);
        }
      } else {
        Analytics.track(`Failed to use credit card`, {
          ...extractCheckoutError(error),
          stage: 'complete_checkout',
          code: activeCode,
          secondChance: isSecondChance,
        });
        setStripeError('Could not complete checkout');
        onPurchaseFailure && onPurchaseFailure();
      }
    } else if (result.error) {
      setProcessing(false);
      Analytics.track(`Failed to use credit card`, {
        errorMessage: result.error.message,
        stripeCode: result.error.code,
        stripeType: result.error.type,
        stripeDeclineCode: result.error.decline_code,
        stage: 'confirm_card_setup',
        code: activeCode,
        secondChance: isSecondChance,
      });
      // This point will only be reached if there is an immediate error when
      // confirming the payment - for example, payment details incomplete
      if (result.error.message) {
        setStripeError(result.error.message);
      } else {
        setStripeError('Could not complete checkout');
      }
    } else {
      Analytics.track(`Failed to use credit card`, {code: activeCode, secondChance: isSecondChance});
    }
    setProcessing(false);
  };

  const confirmPayment = async (event: any) => {
    event.preventDefault();
    await runCardCheckout(code, false);
  };

  if (paymentRequestLoading) {
    return null;
  }

  // 2025-08-11 Trying to rectify some odd behavior in the decision to show the credit card inputs or not
  // previously, if we determined we needed a valid email because:
  //  user was brand new - if they were going to use Apple Pay, we'll create an account with that email
  //  user had a phone number - we need an email to send Stripe communication to
  // we would make that decision based on the token. If the token wasn't present or didn't look like an email, we'd put the user into the above buckets
  // if the token was an email, we wouldn't show certain elements
  //
  // however, we now pass a person_id in as the token, meaning that by our token logic, we'd say they need to see the credit card fields.
  // the correct logic is just to hide those fields if a user can use Apple Pay / Google Pay
  // QA-only: force the custom CTA to render and lay out exactly like the Apple Pay path, even on
  // builds where Apple Pay is unavailable (e.g. local dev over LAN, where Apple Pay domain
  // validation fails so `paymentRequest` is undefined and the wallet block never renders).
  // Opt in with ?qaForceCTA=1; hard-disabled in production. The button is non-functional under this
  // flag (no real payment request) — it exists purely to verify on-screen placement / fit.
  const qaForceCustomCTA =
    !Environment.isProduction && typeof window !== 'undefined' && getSearchParams().get('qaForceCTA') === '1';

  // True when the layout should behave as if a wallet (Apple/Google Pay) is present: hide the email
  // + card inputs until the user explicitly taps "Or use a credit card". Preserves prior behavior
  // exactly when qaForceCustomCTA is false.
  const treatWalletAvailable =
    qaForceCustomCTA ||
    (useUpdatedCreditCardLogic && growthBook.getFeatureValue('hide_web_credit_card_if_apple_pay', false));

  const shouldShowEmailInput = treatWalletAvailable ? shouldAskForEmail && showCreditCardInput : shouldAskForEmail;
  const shouldShowCreditCardInputs = treatWalletAvailable
    ? showCreditCardInput
    : shouldAskForEmail || showCreditCardInput;

  const cardElementOptions = {
    iconStyle: 'solid' as const,
    style: {
      base: {
        lineHeight: '3em',
        backgroundColor: '#30313d',
        iconColor: '#c4f0ff',
        color: '#fff',
        fontWeight: 300,
        '::placeholder': {
          color: '#fff',
        },
      },
      invalid: {
        iconColor: '#FFC7EE',
        color: '#FFC7EE',
      },
    },
  };
  const cardFieldWrapperStyle: React.CSSProperties = {
    border: '1px solid #4b4d5e',
    borderRadius: '4px',
    backgroundColor: '#30313d',
    padding: '1.5px',
    paddingLeft: '12px',
  };
  const emailInputStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: '#30313d',
    border: '1px solid #4b4d5e',
    fontWeight: 400,
    height: '48px',
    fontFamily: 'Nunito Sans, Helvetica Neue, Helvetica, Arial, sans-serif',
    ...stripeStyle,
  };

  // I21 — the offer sheet's card form. Stripe allows one card Element per Elements
  // instance, so the paywall's own card field unmounts while the offer sheet is open.
  const secondChanceCardForm = (
    <>
      {shouldAskForEmail ? (
        <input
          className="text-white email-input"
          style={emailInputStyle}
          placeholder={t('common.Email')}
          type="email"
          name="email"
          value={email}
          onChange={e => {
            setEmail(e.target.value);
            setAccountCreationError(false);
          }}
        />
      ) : null}
      <div className="border-solid" style={cardFieldWrapperStyle}>
        <CardElement options={cardElementOptions} />
      </div>
      {accountCreationError ? (
        <p className="text-red-1 text-sm font-bold">{t('common.this_email_is_not_available')}</p>
      ) : null}
      {stripeError.length > 0 ? <p className="text-red-1 text-sm">{stripeError}</p> : null}
    </>
  );

  const showCustomCTA =
    (useCustomCTA && growthBook.getFeatureValue('paywall_link_out_custom_cta', false)) || qaForceCustomCTA;

  // I21 — second-chance offer sheet actions. Accept with a wallet re-shows the native
  // Apple/Google Pay sheet at the offer's terms; the sheet's card form pays through
  // runCardCheckout at the offer's price code (and is the only option without a wallet).
  const handleSecondChanceAccept = () => {
    // Never start a wallet payment while a card payment from the offer form is running.
    if (!paymentRequest || !secondChanceOffer || processing) return;
    Analytics.track(PAYWALL_EVENTS.dismissOfferAccepted, {
      paywall,
      code,
      offerCode: secondChanceOffer.code,
      source: secondChanceSourceRef.current,
      method: 'wallet',
    });
    // Leave the offer sheet OPEN so the native pay sheet appears over it — if the user
    // cancels the wallet sheet they land back on the offer, and the persistent ✕ still
    // lets them reopen it.
    // Re-open the native pay sheet; the paymentmethod handler completes against the offer
    // code because secondChanceActiveRef is set. show() must run in the click gesture
    // (this handler is invoked from the CTA onClick).
    secondChanceActiveRef.current = true;
    // The paymentRequest was built at mount with the ORIGINAL product's label/amount,
    // so re-showing it as-is would display the original terms (e.g. "7 days then
    // $69.99"). Update the sheet to the offer's terms first — a 14-day free trial is
    // $0 today, matching the same "N days then <annual>" label format as the original.
    try {
      paymentRequest.update({
        total: {
          label: t('checkout_screen.sheet_label', {
            daysFree: secondChanceOffer.daysFree,
            planPrice: secondChanceOffer.annualPrice,
          }),
          amount: 0,
        },
      });
      // The request now carries the offer's terms until the offer sheet closes.
      restorePendingRef.current = true;
    } catch (e) {
      // A failed label update shouldn't block the offer — fall through and show the
      // sheet with the original label rather than dropping the accept entirely.
      Sentry.captureException(e instanceof Error ? e : new Error(String(e)));
    }
    // show() can throw synchronously or return a promise that rejects. Either way the
    // attempt is over; the offer's terms are restored when the offer sheet closes
    // (restorePendingRef), same as after a cancel.
    const onShowFailed = (e: unknown) => {
      secondChanceActiveRef.current = false;
      Sentry.captureException(e instanceof Error ? e : new Error(String(e)));
    };
    try {
      Promise.resolve(paymentRequest.show() as unknown).catch(onShowFailed);
    } catch (e) {
      onShowFailed(e);
    }
  };

  const handleSecondChanceCardSubmit = () => {
    if (!secondChanceOffer) return;
    Analytics.track(PAYWALL_EVENTS.dismissOfferAccepted, {
      paywall,
      code,
      offerCode: secondChanceOffer.code,
      source: secondChanceSourceRef.current,
      method: 'card',
    });
    runCardCheckout(secondChanceOffer.code, true);
  };

  const handleSecondChanceDecline = () => {
    Analytics.track(PAYWALL_EVENTS.dismissOfferDeclined, {
      paywall,
      code,
      offerCode: secondChanceOffer?.code,
      source: secondChanceSourceRef.current,
    });
    setShowSecondChanceOffer(false);
    // A card error from the offer form shouldn't linger on the paywall's card form.
    setStripeError('');
    // The pay sheet is closed by the time the user can act on the offer sheet, so the
    // deferred restore of the selected option's terms is safe here.
    if (restorePendingRef.current) {
      restorePendingRef.current = false;
      try {
        onSecondChanceDismissed && onSecondChanceDismissed();
      } catch (e) {
        Sentry.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    }
  };

  return (
    <>
      {secondChanceOffer ? (
        <SecondChanceOfferSheet
          isOpen={showSecondChanceOffer}
          offer={secondChanceOffer}
          originalDaysFree={daysFree ?? 0}
          paymentMethod={
            paymentRequest
              ? paymentMethod
              : qaForceCustomCTA
              ? /iPhone|iPad/.test(navigator.userAgent)
                ? 'Apple Pay'
                : 'Google Pay'
              : undefined
          }
          // ?qaForceCTA=1 (non-production) also previews the sheet's wallet layout without a
          // real wallet; REDEEM is inert there, like the forced paywall CTA.
          walletAvailable={!!paymentRequest || qaForceCustomCTA}
          cardForm={secondChanceCardForm}
          canSubmitCard={!shouldAskForEmail || validateEmail(email)}
          processing={processing}
          onSubmitCard={handleSecondChanceCardSubmit}
          onAccept={handleSecondChanceAccept}
          onDecline={handleSecondChanceDecline}
        />
      ) : null}
      <Scroll el={el} onElReached={() => Analytics.track('Stripe Form Scrolled into View')}>
        {paymentRequest || qaForceCustomCTA ? (
          <div className="mt-4">
            {accountCreationError && (
              <text className="text-red-1 text-sm font-bold">{t('common.this_email_is_not_available')}</text>
            )}

            {showCustomCTA ? (
              <CustomPaymentRequestButton
                paymentRequest={paymentRequest}
                onClick={() => {
                  onCheckoutStarted && onCheckoutStarted();
                  Analytics.track('Custom CTA Clicked');
                }}
                rounded="full"
                loading={processing}
                disabled={processing}
              >
                {customCTAText ? customCTAText : 'TRY NOW'}
              </CustomPaymentRequestButton>
            ) : (
              <PaymentRequestButtonElement
                options={{
                  style: {
                    paymentRequestButton: {
                      type: 'subscribe',
                      theme: 'light',
                    },
                  },
                  // showCustomCTA is true whenever qaForceCustomCTA is, so paymentRequest is set here.
                  paymentRequest: paymentRequest!,
                }}
                onClick={() => {
                  onCheckoutStarted && onCheckoutStarted();
                  Analytics.track('Standard CTA Clicked');
                }}
              />
            )}

            <p className="text-white text-xs mt-2" style={{textAlign: 'center'}}>
              <button
                type="button"
                onClick={() => {
                  setShowCreditCardInput(true);
                  onCheckoutStarted && onCheckoutStarted();
                  onCreditCardChoice && onCreditCardChoice();
                }}
              >
                {' '}
                {paymentMethod && showCustomCTA ? `Start now securely using ${paymentMethod}.` : null}
                <p className="underline">Or use a credit card</p>
              </button>
            </p>
          </div>
        ) : null}
        <form className={`${paymentRequest ? '' : 'mt-3'}`} onSubmit={confirmPayment}>
          {shouldShowEmailInput ? (
            <>
              {accountCreationError && !paymentRequest && (
                <text className="text-red-1 text-sm font-bold">{t('common.this_email_is_not_available')}</text>
              )}
              <input
                disabled={stripeError.length > 0}
                className="mt-2 text-white email-input"
                style={{
                  width: '100%',
                  backgroundColor: '#30313d',
                  border: '1px solid #4b4d5e',
                  fontWeight: 400,
                  height: '48px',
                  fontFamily: 'Nunito Sans, Helvetica Neue, Helvetica, Arial, sans-serif',
                  ...stripeStyle,
                }}
                placeholder={t('common.Email')}
                type="email"
                name="email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setAccountCreationError(false);
                }}
                onFocus={() => {
                  onCheckoutStarted && onCheckoutStarted();
                  Analytics.track('Email Field Focused');
                }}
                ref={ref => (el.current = ref)}
              />
            </>
          ) : null}
          {(shouldShowCreditCardInputs || (!paymentRequest && !qaForceCustomCTA)) &&
          !paymentRequestLoading &&
          !showSecondChanceOffer ? (
            <div
              className="mt-2 mb-2 border-solid"
              style={{
                border: '1px solid #4b4d5e',
                borderRadius: '4px',
                backgroundColor: '#30313d',
                padding: '1.5px',
                paddingLeft: '12px',
              }}
            >
              <CardElement
                options={cardElementOptions}
                onFocus={() => {
                  onCheckoutStarted && onCheckoutStarted();
                  Analytics.track('Credit Card Form Focused');
                }}
              />
            </div>
          ) : null}

          {stripeError.length > 0 ? <text className="text-red-1 text-sm">{stripeError}</text> : null}

          {(shouldShowCreditCardInputs || (!paymentRequest && !qaForceCustomCTA)) && !paymentRequestLoading ? (
            processing ? (
              <ButtonProcessing className={'max-w-full'} testId="processingButton" title={t('common.processing')} />
            ) : (
              <button
                id="submit"
                disabled={shouldShowEmailInput && !validateEmail(email)}
                className={`mb-2 btn max-w-full  transition-opacity duration-500 ease-in-out h-16 ${
                  shouldShowEmailInput && !validateEmail(email) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {ctaText ?? buttonText(isWinback(), daysFree)}
              </button>
            )
          ) : null}
        </form>
      </Scroll>
    </>
  );
};

export default PaymentIntentScreen;
